import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Eye, EyeOff } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const u = await login(email, password);
      if (u.role === "admin") nav("/admin");
      else if (u.role === "driver") nav("/driver");
      else nav("/customer");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      <div className="hidden lg:flex flex-col justify-between p-12 w-1/2" style={{ background: "linear-gradient(135deg, #061224 0%, #0B3087 60%, #E62E2D 130%)" }}>
        <Logo />
        <div className="text-white">
          <h2 className="font-display text-5xl uppercase leading-tight">Welcome back.<br/><span className="text-asap-yellow">The road awaits.</span></h2>
          <p className="text-white/70 mt-4 max-w-md">Customer, driver, or admin — log in to dispatch or get dispatched in seconds.</p>
        </div>
        <div className="text-white/40 text-xs">© ASAP Roadside</div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <form onSubmit={submit} className="w-full max-w-md space-y-5" data-testid="login-form">
          <div className="lg:hidden mb-6"><Logo /></div>
          <div>
            <div className="text-xs tracking-[0.3em] uppercase text-asap-orange font-bold">Log In</div>
            <h1 className="font-display text-3xl uppercase text-asap-deep mt-1">Sign in to ASAP</h1>
          </div>

          <Field label="Email">
            <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-4 py-3 border-2 border-asap-deep focus:border-asap-orange focus:outline-none font-medium" placeholder="you@example.com" />
          </Field>
          <Field label="Password">
            <div className="relative">
              <input data-testid="login-password" type={showPw ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)} className="w-full px-4 py-3 pr-12 border-2 border-asap-deep focus:border-asap-orange focus:outline-none font-medium" placeholder="••••••••" />
              <button type="button" onClick={() => setShowPw(!showPw)} data-testid="login-toggle-pw" className="absolute right-3 top-1/2 -translate-y-1/2 text-asap-deep hover:text-asap-orange" aria-label={showPw ? "Hide password" : "Show password"}>
                {showPw ? <EyeOff size={18}/> : <Eye size={18}/>}
              </button>
            </div>
          </Field>

          {error && <div data-testid="login-error" className="text-asap-red text-sm font-bold border-2 border-asap-red bg-red-50 px-3 py-2">{error}</div>}

          <button data-testid="login-submit" type="submit" disabled={busy} className="w-full px-6 py-3 bg-asap-yellow text-asap-deep font-display uppercase tracking-wide border-2 border-asap-deep shadow-hard hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-hard-sm transition-all disabled:opacity-60">
            {busy ? "Signing in…" : "Log In →"}
          </button>

          <div className="text-sm text-slate-600 flex justify-between items-center">
            <span>New here? <Link data-testid="login-to-register" to="/register" className="font-bold text-asap-orange hover:underline">Create an account</Link></span>
            <Link data-testid="login-forgot" to="/forgot-password" className="text-xs text-asap-deep hover:text-asap-orange font-bold uppercase tracking-wider">Forgot password?</Link>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs tracking-[0.2em] uppercase font-bold text-asap-deep mb-1">{label}</div>
      {children}
    </label>
  );
}
